// Camera-relative rendering.
//
// The authoritative position of everything is a heliocentric ecliptic J2000
// vector in metres, held in doubles on the CPU. float32 has a 24-bit mantissa,
// so a position near Mars at 1.5e11 m quantises to steps of about 16 km and a
// boulder would jitter by kilometres. Each frame every registered node is
// written as (world - camera), which puts the camera at the scene origin and
// spends the whole float32 mantissa on the distance that is actually on screen.

import type * as THREE from 'three';

/** Metres, heliocentric ecliptic J2000, doubles. */
export interface WorldVec {
  x: number;
  y: number;
  z: number;
}

export interface WorldFrameHandle {
  /** Every registered node is a direct child of this. */
  readonly root: THREE.Object3D;
  /** Returns the unregister closure, the way every dispose in this repo does. */
  register(node: THREE.Object3D, at: () => WorldVec): () => void;
  /** Once per frame, before the draw. */
  applyCameraRelative(cameraWorld: WorldVec): void;
  count(): number;
  dispose(): void;
}

export interface WorldFrameConfig {
  root: THREE.Object3D;
  /** Named in the assert, so a failure says which frame it came from. */
  label: string;
}

function describeNode(node: THREE.Object3D): string {
  return `${node.name || node.type} (uuid ${node.uuid})`;
}

export function createWorldFrame(config: WorldFrameConfig): WorldFrameHandle {
  const nodes = new Map<THREE.Object3D, () => WorldVec>();
  const { root, label } = config;
  // applyCameraRelative runs from the render loop ahead of every
  // requestAnimationFrame, so throwing here freezes the whole page over one
  // stray child. Report each offending node once and keep rendering.
  const reported = new WeakSet<THREE.Object3D>();

  return {
    root,
    register(node: THREE.Object3D, at: () => WorldVec): () => void {
      nodes.set(node, at);
      if (node.parent !== root) root.add(node);
      return () => {
        nodes.delete(node);
        if (node.parent === root) root.remove(node);
      };
    },
    applyCameraRelative(cameraWorld: WorldVec): void {
      for (const [node, at] of nodes) {
        const p = at();
        node.position.set(p.x - cameraWorld.x, p.y - cameraWorld.y, p.z - cameraWorld.z);
      }
      // A node parented to the world root but never registered keeps whatever
      // position it was given, which reads as a body frozen at the origin
      // rather than as a bug; a registered node detached from root by other
      // code just as silently stops moving. The count is the cheapest thing
      // that catches either direction.
      if (import.meta.env.DEV && root.children.length !== nodes.size) {
        for (const child of root.children) {
          if (nodes.has(child) || reported.has(child)) continue;
          reported.add(child);
          console.error(`world frame ${label}: unregistered child under the world root - `
            + `${describeNode(child)} - every child must be registered`);
        }
        for (const node of nodes.keys()) {
          if (node.parent === root || reported.has(node)) continue;
          reported.add(node);
          console.error(`world frame ${label}: registered node is not a child of the world root - `
            + `${describeNode(node)} - register() should have kept them in sync`);
        }
      }
    },
    count(): number {
      return nodes.size;
    },
    dispose(): void {
      nodes.clear();
    },
  };
}
